import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

/**
 * Utility class for debounce functionality
 * Provides methods to create debounced observables
 */
export class DebounceUtils {
  /**
   * Creates a debounced observable from a subject
   * @param subject The subject to debounce
   * @param time The debounce time in milliseconds
   * @returns An observable that emits debounced values
   */
  static debounce<T>(subject: Subject<T>, time = 400): Observable<T> {
    return subject.pipe(
      debounceTime(time),
      distinctUntilChanged()
    );
  }

  /**
   * Creates a debounced function that returns an observable
   * @param fn The function to debounce
   * @param time The debounce time in milliseconds
   * @returns A function that returns an observable of the debounced function results
   */
  static debounceFunction<T, R>(
    fn: (arg: T) => R,
    time = 400
  ): (arg: T) => Observable<R> {
    const subject = new Subject<T>();
    
    return (arg: T): Observable<R> => {
      subject.next(arg);
      return subject.pipe(
        debounceTime(time),
        distinctUntilChanged(),
        switchMap(value => {
          const result = fn(value);
          return result instanceof Observable ? result : new Observable<R>(observer => {
            observer.next(result);
            observer.complete();
          });
        })
      );
    };
  }

  /**
   * Creates a simple debounced function
   * @param fn The function to debounce
   * @param time The debounce time in milliseconds
   * @returns A debounced version of the function
   */
  static debounceSimple<T>(fn: (arg: T) => void, time = 400): (arg: T) => void {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    
    return (arg: T): void => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(arg), time);
    };
  }
}
